/**
 * Issue #644 — THE SEARCH AND THE CARD LIST, SERVER-DRIVEN AND PAGED. Wave 5
 * and the last of epic #609.
 *
 * ## What this module is
 *
 * #643 made the map fetch by viewport, and in doing so made every full-array
 * scan on the page WRONG rather than merely slow: a search that only sees the
 * rows the viewport happened to load is a worse answer than the one #617 was
 * filed to fix. It bridged that by asking for the world box while a term was
 * active — one cached read, bounded and capped, and explicitly temporary:
 * past `GROUNDS_BBOX_MAX_ROWS` it degraded to "zoom in to search here".
 *
 * This is the replacement. The term goes to the server (`GET /api/grounds?q=`),
 * the answer comes back PAGED, and the client stops filtering. What is left
 * here is the arithmetic and the copy: which page, how many, and what the
 * page may honestly say about itself.
 *
 * It is a module rather than code inside `GroundsMap.jsx` for the reason
 * #615, #617 and #643 were each bitten by: that page mounts leaflet, so this
 * repo renders no component of it in a unit test. A rule written inside it
 * can only be grepped. Everything here is an ordinary function over ordinary
 * values.
 *
 * ## The one rule everything below serves
 *
 * **A number on screen may never describe more than it can see.** This repo
 * has shipped that defect three times — #617's "All grounds (1)" inside a
 * closed select, #612's per-page counts on a bulk publish, #608's failed
 * fetch rendered as an empty directory — and every fix was the same: say the
 * true thing, and name the scope when the scope is not everything. Paging a
 * list is the fourth chance to make it, which is why `describeDirectoryPage`
 * and `chipCountLabel` both take a scope and neither has a default that
 * quietly means "all".
 *
 * ## Copied from #603, not reinvented
 *
 * `frontend/src/lib/adminVenues.js` already pages a directory — the admin
 * venue console. The parameter shape (a page number that clamps rather than
 * blanks, a page-1 reset on any change to what is being looked for, a count
 * line that states the range AND the total) is taken from it deliberately.
 * The one difference is where the work happens: #603 runs in the browser over
 * a whole directory it has already fetched, and says so in its own header
 * ("revisit when the directory outgrows a single fetch"). This is that
 * revisit, for the map.
 */

/**
 * ROWS PER PAGE — mirrors `GROUNDS_SEARCH_PAGE_SIZE` in
 * `api/src/db/repos/grounds.ts`, which carries the derivation (a card is
 * ~300 px, so a thousand of them is 300,000 px of DOM nobody reaches the
 * bottom of; 100 rows of ~585 B is ~58 KB).
 *
 * The client cannot import it — `api/` is TypeScript compiled for Workers and
 * CRA's module scope refuses an import from outside `src/` — so it is
 * restated, and `directorySearch.test.js` READS THE API SOURCE OFF DISK and
 * asserts the mirror still equals the number it mirrors. That is the same
 * discipline `VIEWPORT_GRID_DEGREES` is held to (#643), for the same reason:
 * a copied constant with no test is drift waiting to happen (`formatKm`,
 * #436).
 *
 * It is needed on both sides because both sides page: the server pages a
 * SEARCH, and the client pages the viewport's rows, which the server has
 * already capped at `GROUNDS_BBOX_MAX_ROWS`. One number, one page size, so a
 * reader does not learn two.
 */
export const GROUNDS_SEARCH_PAGE_SIZE = 100;

/**
 * The longest term the endpoint will answer — mirrors
 * `GROUNDS_SEARCH_MAX_LENGTH`, pinned by the same test. The search box wears
 * it as `maxLength`, so a person cannot type a request the server refuses;
 * the server refuses one anyway, because a bound enforced only in a text
 * input is not a bound.
 */
export const GROUNDS_SEARCH_MAX_LENGTH = 120;

/**
 * HOW LONG TYPING MUST SETTLE BEFORE THE SEARCH IS SENT — 250 ms.
 *
 * Derived the way `VIEWPORT_DEBOUNCE_MS` (300 ms) is, from the two ends of
 * the usable band. The floor is a fast typist: ~200 ms between keystrokes in
 * ordinary prose, so anything shorter issues a request per character and the
 * answers arrive out of order for the whole word. The ceiling is the ~400 ms
 * where a delay stops reading as "loading" and starts reading as "nothing
 * happened".
 *
 * SHORTER than the viewport's 300 ms, deliberately: that one waits for
 * leaflet's own 250 ms zoom animation to finish, and there is no animation
 * here. A search is a question a person just asked with their fingers, and it
 * should be answered sooner than a pan they may still be finishing.
 */
export const SEARCH_DEBOUNCE_MS = 250;

const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

/** A page number that is a positive integer, or 1. Never `NaN`, never 0. */
export function clampPage(page, pageCount) {
  const total = Math.max(1, Math.trunc(pageCount) || 1);
  const wanted = Math.trunc(Number(page)) || 1;
  return Math.min(Math.max(wanted, 1), total);
}

/** The term as the page treats it: trimmed, and "" when there is no search. */
export function searchTerm(raw) {
  return typeof raw === "string" ? raw.trim() : "";
}

/**
 * THE SAME SUBSTRING RULE, IN THE BROWSER — for the ONE set of rows the
 * server cannot match for us.
 *
 * #644 exists to delete a client-side term filter, so a client-side term
 * filter arriving in the same slice needs its reason stated plainly.
 *
 * `GroundsMap.jsx` draws a "played-only" pin for every venue in a player's
 * own match history that is NOT in their visible directory — another user's
 * private ground, resolved by name at read time (`listVenuesPlayedForPlayer`).
 * Those rows come from `/venues/played`, not from `GET /api/grounds`, and no
 * `q` sent to the directory can ever return them: they are outside its
 * visibility scope by construction.
 *
 * So while a search is active they are matched here, and this is NOT the
 * defect the slice removes. That defect was scanning a WHOLE-DIRECTORY array
 * — unbounded, and after #643 not even complete. This set is a player's own
 * match history: bounded by how many matches one person has logged, fetched
 * regardless of any search, and the only alternative is to drop those pins
 * from search results entirely, which would silently lose a real answer to a
 * real question.
 *
 * Same three fields and the same case-insensitive substring as
 * `groundsSearchWhere`, so the two halves of one search cannot disagree about
 * what matches.
 */
export function matchesSearchTerm(row, term) {
  const needle = searchTerm(term).toLowerCase();
  if (!needle) return true;
  return [row?.name, row?.city, row?.country]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(needle));
}

/**
 * THE SEARCH REQUEST — and what it deliberately does NOT carry.
 *
 * No `bbox`, and no `zoom`. This is the viewport-versus-search decision
 * expressed in the one place a reader can check it: a search is a question
 * about the DIRECTORY, not about the screen. You search for a ground
 * precisely because you cannot see it, so bounding the answer by the frame
 * the last pan left the map in would make the search fail exactly when it is
 * needed. The server makes the same call from the other side (`q` outranks
 * `bbox`) and echoes no box, so neither half can quietly start bounding it.
 *
 * This function carries no `include_archived` of its own — #683 made that
 * flag role-conditioned, and the one place that decides it is
 * `directoryLoad.js`'s `directoryBasePath(isAdmin)`, which `searchPath`
 * prepends to whatever this returns. The box — or its absence — has nothing
 * to do with visibility either way.
 */
export function searchQuery(term, page) {
  const trimmed = searchTerm(term);
  if (!trimmed) return "";
  const n = clampPage(page, Number.MAX_SAFE_INTEGER);
  return `q=${encodeURIComponent(trimmed)}&page=${encodeURIComponent(String(n))}`;
}

/** Two search requests the page would answer identically. */
export function searchKey(term, page) {
  const trimmed = searchTerm(term);
  if (!trimmed) return null;
  return `${trimmed}|p${clampPage(page, Number.MAX_SAFE_INTEGER)}`;
}

/**
 * Read the search envelope.
 *
 * Normalises to one shape with `grounds` ALWAYS present — empty, never
 * absent — so the page renders `grounds.map` unconditionally. A bare ARRAY is
 * still accepted and read as an unpaged answer: that is what every other
 * caller of `GET /api/grounds` receives, and a client that threw on it would
 * turn a partial regression into a blank page (`readViewportGrounds` makes
 * the same allowance for the same reason).
 *
 * `matching` falls back to the row count rather than to 0, because 0 would be
 * a claim ("nothing matched") where the fallback means "the server did not
 * say". The rows in hand are the smallest number that is certainly true.
 */
export function readDirectorySearch(data) {
  if (Array.isArray(data)) {
    return {
      grounds: data,
      matching: data.length,
      page: 1,
      pageCount: 1,
      pageSize: data.length || GROUNDS_SEARCH_PAGE_SIZE,
    };
  }
  const body = data || {};
  const grounds = Array.isArray(body.grounds) ? body.grounds : [];
  const pageSize = isFiniteNumber(body.page_size) ? body.page_size : GROUNDS_SEARCH_PAGE_SIZE;
  const pageCount = Math.max(1, isFiniteNumber(body.page_count) ? body.page_count : 1);
  return {
    grounds,
    matching: isFiniteNumber(body.matching) ? body.matching : grounds.length,
    page: clampPage(body.page, pageCount),
    pageCount,
    pageSize,
  };
}

/**
 * THE CARD LIST, AS ONE SHAPE FOR BOTH WAYS IT IS PAGED.
 *
 * The list has two sources and they are paged in two places, which is a
 * seam worth stating rather than hiding:
 *
 *   A SEARCH is paged BY THE SERVER. The rows in hand are already one page,
 *   and slicing them again here would show 100 of 100 rows as ten of them.
 *   The page number, the page count and the true total come off the answer.
 *
 *   THE VIEWPORT'S ROWS are paged HERE. They arrived as one bounded read
 *   (`GROUNDS_BBOX_MAX_ROWS`, at most 1,000) because a map draws pins for all
 *   of them at once; the CARD list underneath cannot render a thousand, so it
 *   takes a page at a time.
 *
 * `rows` is what the page will actually render, AFTER the chips and near-me
 * have narrowed it — so `total` describes the narrowed set, never the set
 * before it. `narrowed` says whether that happened, because it is what
 * decides whether the server's total still describes what is on screen.
 *
 * `scope` is the answer to "what does `total` count", and every caller must
 * use it rather than assume:
 *
 *   "all"  — every matching ground in the directory. Only ever a search that
 *            nothing on the client has narrowed.
 *   "page" — the rows on THIS page. A search whose results a chip is
 *            narrowing: later pages may hold more matches the chip would
 *            keep, and this client cannot know how many without asking.
 *   "here" — the grounds in the viewport. The unsearched list, which is
 *            bounded by the screen since #643 and says so.
 */
export function directoryListing({ rows, answer, page, narrowed, truncated, mode }) {
  const all = Array.isArray(rows) ? rows : [];
  if (answer) {
    const pageCount = Math.max(1, answer.pageCount || 1);
    const current = clampPage(answer.page, pageCount);
    const pageSize = answer.pageSize || GROUNDS_SEARCH_PAGE_SIZE;
    const start = (current - 1) * pageSize;
    return {
      rows: all,
      page: current,
      pageCount,
      pageSize,
      from: all.length === 0 ? 0 : start + 1,
      to: start + all.length,
      // Narrowed, the server's total no longer describes what is on screen.
      total: narrowed ? all.length : answer.matching,
      scope: narrowed ? "page" : "all",
      // `partial` is "the rows in hand are not every row that matches" — what
      // the chip counts have to admit to. A single-page search is complete
      // even when a chip is narrowing it, because there is no other page.
      partial: pageCount > 1,
      pagedByServer: true,
      // Issue #691 — a server-paged search always has rows to count against
      // (there is no cells-mode equivalent for a search answer), so the chips
      // stay counted here. See the `false` case below for why this exists.
      counted: true,
    };
  }
  const pageCount = Math.max(1, Math.ceil(all.length / GROUNDS_SEARCH_PAGE_SIZE));
  const current = clampPage(page, pageCount);
  const start = (current - 1) * GROUNDS_SEARCH_PAGE_SIZE;
  const slice = all.slice(start, start + GROUNDS_SEARCH_PAGE_SIZE);
  return {
    rows: slice,
    page: current,
    pageCount,
    pageSize: GROUNDS_SEARCH_PAGE_SIZE,
    from: slice.length === 0 ? 0 : start + 1,
    to: start + slice.length,
    total: all.length,
    scope: "here",
    // The viewport read itself can be short — the row cap bit, or the server
    // answered in counts rather than rows — and then even "here" is not all
    // of here.
    partial: truncated === true || mode === "cells",
    pagedByServer: false,
    // Issue #691 — in cells mode the server answered in counts per area, not
    // rows, so `played`/`mine`/`global`/the type chips have NOTHING to count:
    // not a small number, not a page-scoped number, nothing. `rows` here can
    // still be non-empty (#688's saved/played fold survives cells mode), but
    // those personal rows are not a sample of the viewport and counting them
    // as one would be a worse lie than "0 here" ever was — see
    // `chipCountLabel`. Named on the listing rather than re-derived from
    // `mode` at each of the six call sites, so the call sites stay simple
    // booleans on a value they already have.
    counted: mode !== "cells",
  };
}

/** `1000` -> `1,000`, without asking what locale the browser is in. */
const withThousands = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * `match` -> `matches`, `ground` -> `grounds`. The sibilant rule rather than a
 * bare `+ "s"`, because this file's two nouns fall on opposite sides of it and
 * "1 matchs" is the kind of thing a reader stops trusting a count line over.
 */
const plural = (n, word) => {
  if (n === 1) return word;
  return /(?:ch|sh|s|x|z)$/.test(word) ? `${word}es` : `${word}s`;
};

/**
 * THE COUNT LINE — the range AND the total, every time, with the scope named
 * whenever the total is not "everything that matches".
 *
 * #603 requires the range and the total together for its own reason: "97
 * venues" under a filter reads as the size of the directory unless the
 * sentence says otherwise. Paging adds a second way to mislead — a total
 * computed over one page — so the scope word is not decoration here, it is
 * the difference between a fact and #612's bug.
 *
 * Returns `null` when there is nothing to say: no rows and no search, which
 * is the empty state's business (`DirectoryGridEmpty`) rather than a count
 * line reading "Showing 0–0 of 0".
 */
export function describeDirectoryPage(listing) {
  if (!listing || listing.total === 0) return null;
  const { from, to, total, scope, page, pageCount } = listing;
  const range = from === to ? `${from}` : `${from}–${to}`;
  if (scope === "all") {
    return `Showing ${range} of ${withThousands(total)} ${plural(total, "match")}`;
  }
  if (scope === "page") {
    return `Showing ${withThousands(total)} ${plural(total, "match")} on page ${page} of ${pageCount}`;
  }
  return `Showing ${range} of ${withThousands(total)} ${plural(total, "ground")} here`;
}

/**
 * A FILTER CHIP'S LABEL, WITH ITS COUNT AND — WHEN IT MATTERS — ITS SCOPE.
 *
 * The chips count `played`, `mine` and `global` over the rows the page is
 * HOLDING, which is one page of a search or one viewport of the directory.
 * Those three cannot come off the server: "where I've played" is answered by
 * `/venues/played`, "mine" is a bookmark in another table, and neither is a
 * predicate `GET /api/grounds` knows about. #644 allows exactly this — the
 * counts are server-derived OR honestly relabelled — and relabelling is the
 * option that does not invent three new joins for a dropdown.
 *
 * So when the rows in hand ARE every matching row, the label is unchanged:
 * `All grounds (12)`, exactly as it has always read. When they are not, the
 * label says which: `All grounds (12 on this page)` for a paged search,
 * `All grounds (12 here)` for a viewport-bounded list. That is the whole
 * difference between this and the "All grounds (1)" #617 replaced — that one
 * had no scope word available to it, and nobody had noticed it needed one.
 *
 * Issue #691 — `listing.counted === false` (cells mode) is a FOURTH case, and
 * it is not "the count is small", it is "there is no count at all": the
 * bubbles behind these chips total thousands, and reporting `(0 here)` or,
 * once #688 folds a player's own rows back in, `(3 here)` beside them reads
 * as broken or as an outright lie about how many grounds are in the world.
 * The chip keeps working as a filter; it just stops claiming a number it
 * does not have — `label` alone, same as it read before #617 gave every chip
 * a count at all.
 */
export function chipCountLabel(label, count, listing) {
  if (listing && listing.counted === false) return label;
  const n = isFiniteNumber(count) ? count : 0;
  if (!listing || !listing.partial) return `${label} (${n})`;
  return `${label} (${n} ${listing.pagedByServer ? "on this page" : "here"})`;
}

/**
 * ANY CHANGE TO WHAT IS BEING LOOKED FOR RESETS TO PAGE 1 — #603's rule, and
 * its reason holds here unchanged: narrowing the list while on page 3 leaves
 * a reader staring at an empty page indistinguishable from "nothing matches",
 * which is the paging bug every list makes once.
 *
 * Written as a comparison rather than as a setter so the page can call it
 * from a `useMemo` over the values it already has, instead of remembering to
 * reset in five event handlers — the version of this rule that survives a
 * sixth handler being added.
 */
export function listingResetKey({ term, sourceFilter, typeFilter, nearMe }) {
  return [searchTerm(term), sourceFilter || "all", typeFilter || "all", nearMe || "idle"].join("|");
}

/**
 * WHAT THE #617 SUMMARY LINE MAY CLAIM, once the answer is paged.
 *
 * `searchResultSummary` counts the rows it is handed. That was exactly right
 * while the client held every match; with the server paging, the rows in hand
 * are 100 of 350 and counting them would under-report by 250.
 *
 * So the summary is told the TOTAL when the total is known and still
 * describes what is on screen — a search nothing on the client has narrowed —
 * and told nothing when it does not, in which case it counts the rows and
 * says they are this page's. Never a total from the server beside a list the
 * chips have cut down: that is #617's own defect ("a count above the map the
 * grid below does not show") in a new costume.
 */
export function searchSummaryScope(listing) {
  if (!listing || !listing.pagedByServer) return {};
  if (listing.scope === "all") return { total: listing.total };
  return { partial: listing.partial };
}
